import { useQuery } from '@tanstack/react-query';
import type { AuditEntryView, ClientView } from '@lp/contracts';
import { ArrowLeft, Network, Pencil, Ticket } from 'lucide-react';
import { useState } from 'react';
import { useIsSuperAdmin } from '@/lib/auth';
import { Button } from '@/components/ui';
import { Link, useParams, useSearchParams } from 'react-router';
import { errorText, get } from '@/lib/api';
import { age, date, dateTime, num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Card, Empty, ErrorBox, Kpi, Loading, PageHeader, Tabs } from '@/components/ui';
import { ClientStateActions, CompanyUsers, EditClientDialog } from '@/components/client-lifecycle';
import { ClientPackage } from '@/components/client-package';
import { ClientSettings } from '@/components/client-settings';
import { ClientDomains } from './domains';
import { PriceDialog, PriceText } from '@/components/edit-dialogs';
import { useQueryClient } from '@tanstack/react-query';
import { put } from '@/lib/api';
import { ClientProducts } from '@/components/client-products';
import { StateBadge, useClients } from './clients';

/**
 * One client (master prompt §19: Overview · Configuration · Products · Users · Agents · Domains · Activity · Health · Overrides), one tab at
 * a time (progressive disclosure). Overview holds the health; Configuration can show the overrides only; the agents are in Hierarchy.
 */
type ClientTab = 'overview' | 'products' | 'configuration' | 'users' | 'domains' | 'activity';
const CLIENT_TABS: ClientTab[] = ['overview', 'products', 'configuration', 'users', 'domains', 'activity'];
export function ClientPage() {
  const t = useT();
  const id = Number(useParams().clientId);
  const client = useQuery({ queryKey: ['client', id], queryFn: () => get<ClientView>(`/platform/clients/${id}`), enabled: Number.isFinite(id) });
  const list = useClients();
  const activity = useQuery({ queryKey: ['audit', 'client', id], queryFn: () => get<AuditEntryView[]>(`/platform/audit?clientId=${id}&limit=20`), enabled: Number.isFinite(id) });
  const row = list.data?.items.find((r) => r.id === id);
  const superAdmin = useIsSuperAdmin();
  const [editing, setEditing] = useState(false);
  const [pricing, setPricing] = useState(false);
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = (CLIENT_TABS.includes(params.get('tab') as ClientTab) ? params.get('tab') : 'overview') as ClientTab;
  const c = client.data;

  if (client.isLoading || list.isLoading) return <Loading />;
  if (client.error) return <ErrorBox text={errorText(client.error)} />;
  if (!c || !row) return <Empty title={t('Client not found')} />;

  return (
    <>
      <Link to="/clients" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('All clients')}
      </Link>
      <PageHeader
        title={c.nameEn}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{c.code}</span>
            <span>{c.nameKm}</span>
            <StateBadge state={c.state} />
          </span>
        }
        actions={
          <>
          <ClientStateActions client={c} />
          <Link to={`/clients/${c.id}/hierarchy`} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3.5 text-sm font-medium text-ink-800 hover:bg-ink-50">
            <Network size={14} /> {t('Hierarchy')}
          </Link>
          <Link to={`/clients/${c.id}/tickets`} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3.5 text-sm font-medium text-ink-800 hover:bg-ink-50">
            <Ticket size={14} /> {t('Tickets')}
          </Link>
          </>
        }
      />

      <Tabs<ClientTab>
        value={tab}
        onChange={(v) => setParams(v === 'overview' ? {} : { tab: v }, { replace: true })}
        items={[
          { value: 'overview', label: t('Overview') },
          { value: 'products', label: t('Products') },
          { value: 'configuration', label: t('Configuration') },
          { value: 'users', label: t('Users') },
          { value: 'domains', label: t('Domains') },
          { value: 'activity', label: t('Activity') },
        ]}
      />

      {tab === 'overview' && (
      <>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={t('Package')} value={row.package?.name ?? '—'} sub={row.package ? row.package.nameKm : t('No package yet')} />
        <Kpi label={t('Users')} value={num(row.users)} />
        <Kpi
          label={t('Price')}
          value={
            <span className="text-base">
              <PriceText price={c.price} />
            </span>
          }
          sub={
            <span className="flex items-center gap-2">
              {c.price ? t(c.price.source === 'client' ? 'Own price' : 'The package’s price') : t('No price yet')}
              {superAdmin && (
                <button className="text-brand-700 hover:underline" onClick={() => setPricing(true)}>
                  {t('Change')}
                </button>
              )}
            </span>
          }
        />
        <Kpi label={t('Last activity')} value={<span className="text-base">{age(row.lastActivityAt)}</span>} sub={dateTime(row.lastActivityAt)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title={t('Health')} className="lg:col-span-2">
          {row.problems.length === 0 ? (
            <p className="text-sm text-ink-600">{t('Nothing is wrong with this client.')}</p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {row.problems.map((p, i) => (
                <li key={`${p.kind}-${i}`} className="flex items-start gap-2 py-2 text-sm">
                  <Badge tone={p.severity === 'error' ? 'red' : 'amber'}>{t(p.severity === 'error' ? 'Error' : 'Warning')}</Badge>
                  <span>{p.message}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card
          title={t('Details')}
          actions={
            superAdmin && (
              <Button variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(true)} className="h-7 px-2 text-xs">
                {t('Edit details')}
              </Button>
            )
          }
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-ink-500">{t('Address')}</dt>
            <dd className="font-mono text-xs">{c.address}</dd>
            <dt className="text-ink-500">{t('Time zone')}</dt>
            <dd>{c.timezone}</dd>
            <dt className="text-ink-500">{t('Currency')}</dt>
            <dd>{c.currency}</dd>
            <dt className="text-ink-500">{t('Contact')}</dt>
            <dd>{[c.contactName, c.contactPhone, c.contactEmail].filter(Boolean).join(' · ') || '—'}</dd>
            <dt className="text-ink-500">{t('Client since')}</dt>
            <dd>{date(c.createdAt)}</dd>
          </dl>
        </Card>
      </div>

      </>
      )}

      {tab === 'products' && (
        <>
          <ClientPackage clientId={c.id} />
          <ClientProducts clientId={c.id} />
        </>
      )}
      {tab === 'configuration' && <ClientSettings clientId={c.id} />}
      {tab === 'domains' && <ClientDomains clientId={c.id} />}
      {tab === 'users' && <CompanyUsers clientId={c.id} />}
      {editing && <EditClientDialog client={c} onClose={() => setEditing(false)} />}
      {pricing && (
        <PriceDialog
          title={`${t('Price')} — ${c.code}`}
          price={c.price?.source === 'client' ? c.price : null}
          clearLabel={t('Use the package’s price')}
          onSave={async (price) => {
            await put(`/platform/clients/${c.id}/price`, { price });
            void qc.invalidateQueries({ queryKey: ['client', c.id] });
            void qc.invalidateQueries({ queryKey: ['clients'] });
          }}
          onClose={() => setPricing(false)}
        />
      )}

      {tab === 'activity' && (
      <Card title={t('Activity')} className="mt-4">
        {activity.isLoading ? (
          <Loading />
        ) : !activity.data?.length ? (
          <p className="text-sm text-ink-600">{t('Nothing yet.')}</p>
        ) : (
          <ul className="divide-y divide-ink-100 text-sm">
            {activity.data.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5">
                <span className="w-32 shrink-0 text-xs text-ink-500">{dateTime(e.at)}</span>
                <span className="font-mono text-xs">{e.action}</span>
                <span className="text-xs text-ink-500">{e.actor.username ?? t('System')}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}
    </>
  );
}
